import { create } from 'zustand';

/*
 * Installing the portal as an app (PWA, docs/modules/notifications.md "Installable app"). Chromium browsers offer the
 * installation through the `beforeinstallprompt` event, which must be caught early and kept until the user asks; Safari on
 * iOS has no such event — the user adds the app to the home screen from the share sheet, so the menu explains how.
 */

/** The non-standard event Chromium fires when the app can be installed. */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallState {
  /** The browser's offer, kept until it is used (null: none, or already used). */
  offer: BeforeInstallPromptEvent | null;
  /** The app was installed from this browser during this visit. */
  installedNow: boolean;
}

export const useInstallState = create<InstallState>()(() => ({ offer: null, installedNow: false }));

/** Starts listening for the install offer: call once, before the app renders (the event can fire very early). */
export function initInstallPrompt(target: Window = window): void {
  target.addEventListener('beforeinstallprompt', (event) => {
    // The browser's own mini bar is replaced by the entry of the user menu.
    event.preventDefault();
    useInstallState.setState({ offer: event as BeforeInstallPromptEvent });
  });
  target.addEventListener('appinstalled', () => useInstallState.setState({ offer: null, installedNow: true }));
}

/** Whether the app runs as an installed app (its own window, no browser bar). */
export function isStandalone(target: Window = window): boolean {
  const iosStandalone = (target.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return (
    iosStandalone ||
    ['standalone', 'window-controls-overlay', 'minimal-ui', 'fullscreen'].some(
      (mode) => typeof target.matchMedia === 'function' && target.matchMedia(`(display-mode: ${mode})`).matches,
    )
  );
}

/** iPhone, iPad (also the iPadOS that presents itself as a Mac) — where an app is added from the share sheet. */
export function isIos(target: Window = window): boolean {
  const { userAgent, platform, maxTouchPoints } = target.navigator;
  return /iPad|iPhone|iPod/u.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1);
}

export type InstallAvailability =
  /** Running as an installed app. */
  | 'installed'
  /** The browser can install it right now (`prompt()`). */
  | 'prompt'
  /** iOS: no prompt exists, the user adds it from the share sheet. */
  | 'ios'
  /** Nothing to offer (a browser that cannot install, or the offer has not come yet). */
  | 'unavailable';

/** What the menu offers. Pure, so it is tested without a browser. */
export function availabilityOf(state: InstallState, standalone: boolean, ios: boolean): InstallAvailability {
  if (standalone || state.installedNow) return 'installed';
  if (state.offer) return 'prompt';
  return ios ? 'ios' : 'unavailable';
}

export function useInstallAvailability(): InstallAvailability {
  return useInstallState((state) => availabilityOf(state, isStandalone(), isIos()));
}

/** Shows the browser's install dialog; resolves to the user's choice, or `unavailable` when there is no offer. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const { offer } = useInstallState.getState();
  if (!offer) return 'unavailable';
  // An offer can be used once.
  useInstallState.setState({ offer: null });
  try {
    await offer.prompt();
    return (await offer.userChoice).outcome;
  } catch {
    return 'unavailable';
  }
}
